import React, { useState } from 'react';
import { Device, Enterprise, DeviceFilters, SortOptions } from '../../types/deviceTelemetry';

interface DeviceListProps {
  devices: Device[];
  enterprises: Enterprise[];
  filters: DeviceFilters;
  sortOptions: SortOptions;
  onDeviceSelect: (device: Device) => void;
  onFiltersChange: (filters: DeviceFilters) => void;
  onSortChange: (sort: SortOptions) => void;
  loading: boolean;
}

const DeviceList: React.FC<DeviceListProps> = ({
  devices,
  enterprises,
  filters,
  sortOptions,
  onDeviceSelect,
  onFiltersChange,
  onSortChange,
  loading
}) => {
  const [showFilters, setShowFilters] = useState(false);

  // Get all stores from enterprises
  const getAllStores = () => {
    const stores: Array<{ storeId: string; storeName: string; enterpriseId: string }> = [];
    enterprises.forEach(enterprise => {
      enterprise.stores.forEach(store => {
        stores.push({
          storeId: store.storeId,
          storeName: store.storeName,
          enterpriseId: store.enterpriseId
        });
      });
    });
    return stores;
  };

  // Get health score color
  const getHealthScoreColor = (score: number): string => {
    if (score >= 90) return '#22c55e'; // green
    if (score >= 70) return '#eab308'; // yellow
    if (score >= 50) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      online: { color: '#22c55e', text: 'Online', icon: '🟢' },
      offline: { color: '#6b7280', text: 'Offline', icon: '🔴' },
      error: { color: '#ef4444', text: 'Error', icon: '⚠️' },
      maintenance: { color: '#3b82f6', text: 'Maintenance', icon: '🔧' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.offline;
    
    return (
      <span className="status-badge" style={{ backgroundColor: config.color }}>
        {config.icon} {config.text}
      </span>
    );
  };

  // Get device type icon
  const getDeviceTypeIcon = (type: string) => {
    switch (type) {
      case 'POS': return '🖥️';
      case 'TABLET': return '📱';
      case 'MOBILE': return '📲';
      case 'KIOSK': return '🖱️';
      default: return '📱';
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Handle sort column click
  const handleSort = (field: SortOptions['field']) => {
    const newDirection = sortOptions.field === field && sortOptions.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ field, direction: newDirection });
  };

  // Get sort indicator
  const getSortIndicator = (field: SortOptions['field']) => {
    if (sortOptions.field !== field) return '↕️';
    return sortOptions.direction === 'asc' ? '↑' : '↓';
  };

  const stores = getAllStores();

  return (
    <div className="device-list">
      {/* Filters Panel */}
      <div className="filters-section">
        <div className="filters-header">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="filters-toggle"
          >
            🔍 Filters {showFilters ? '↑' : '↓'}
          </button>
          <div className="results-count">
            Showing {devices.length} device{devices.length !== 1 ? 's' : ''}
          </div>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-row">
              <div className="filter-group">
                <label>Search</label>
                <input
                  type="text"
                  placeholder="Search devices, stores, enterprises..."
                  value={filters.search || ''}
                  onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                />
              </div>

              <div className="filter-group">
                <label>Enterprise</label>
                <select
                  value={filters.enterpriseId || ''}
                  onChange={(e) => onFiltersChange({ ...filters, enterpriseId: e.target.value || undefined, storeId: undefined })}
                >
                  <option value="">All Enterprises</option>
                  {enterprises.map(enterprise => (
                    <option key={enterprise.enterpriseId} value={enterprise.enterpriseId}>
                      {enterprise.enterpriseName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Store</label>
                <select
                  value={filters.storeId || ''}
                  onChange={(e) => onFiltersChange({ ...filters, storeId: e.target.value || undefined })}
                  disabled={!filters.enterpriseId}
                >
                  <option value="">All Stores</option>
                  {stores
                    .filter(store => !filters.enterpriseId || store.enterpriseId === filters.enterpriseId)
                    .map(store => (
                      <option key={store.storeId} value={store.storeId}>
                        {store.storeName}
                      </option>
                    ))
                  }
                </select>
              </div>

              <div className="filter-group">
                <label>Device Type</label>
                <select
                  value={filters.deviceType || ''}
                  onChange={(e) => onFiltersChange({ ...filters, deviceType: e.target.value || undefined })}
                >
                  <option value="">All Types</option>
                  <option value="POS">POS Terminal</option>
                  <option value="TABLET">Tablet</option>
                  <option value="MOBILE">Mobile</option>
                  <option value="KIOSK">Kiosk</option>
                </select>
              </div>
            </div>

            <div className="filter-row">
              <div className="filter-group">
                <label>Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => onFiltersChange({ ...filters, status: e.target.value || undefined })}
                >
                  <option value="">All Statuses</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                  <option value="error">Error</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Health Score</label>
                <div className="range-inputs">
                  <input
                    type="number"
                    placeholder="Min"
                    min="0"
                    max="100"
                    value={filters.healthScoreMin || ''}
                    onChange={(e) => onFiltersChange({ ...filters, healthScoreMin: e.target.value ? Number(e.target.value) : undefined })}
                  />
                  <span>to</span>
                  <input
                    type="number"
                    placeholder="Max"
                    min="0"
                    max="100"
                    value={filters.healthScoreMax || ''}
                    onChange={(e) => onFiltersChange({ ...filters, healthScoreMax: e.target.value ? Number(e.target.value) : undefined })}
                  />
                </div>
              </div>

              <div className="filter-group">
                <label>Alert Level</label>
                <select
                  value={filters.alertLevel || ''}
                  onChange={(e) => onFiltersChange({ ...filters, alertLevel: e.target.value || undefined })}
                >
                  <option value="">All Alerts</option>
                  <option value="critical">Critical Only</option>
                  <option value="warning">Warning Only</option>
                  <option value="info">Info Only</option>
                </select>
              </div>

              <div className="filter-group">
                <button
                  onClick={() => onFiltersChange({})}
                  className="clear-filters-button"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Device Table */}
      <div className="devices-table-container">
        <table className="devices-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('deviceName')} className="sortable">
                Device {getSortIndicator('deviceName')}
              </th>
              <th>Type</th>
              <th onClick={() => handleSort('storeName')} className="sortable">
                Store {getSortIndicator('storeName')}
              </th>
              <th onClick={() => handleSort('status')} className="sortable">
                Status {getSortIndicator('status')}
              </th>
              <th onClick={() => handleSort('healthScore')} className="sortable">
                Health Score {getSortIndicator('healthScore')}
              </th>
              <th>Key Metrics</th>
              <th>Alerts</th>
              <th onClick={() => handleSort('lastSeen')} className="sortable">
                Last Seen {getSortIndicator('lastSeen')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="loading-row">
                  <div className="loading-spinner-small"></div>
                  Loading devices...
                </td>
              </tr>
            )}
            
            {!loading && devices.length === 0 && (
              <tr>
                <td colSpan={9} className="no-data-row">
                  No devices match the current filters.
                </td>
              </tr>
            )}
            
            {!loading && devices.map(device => {
              const criticalAlerts = device.alerts.filter(alert => alert.type === 'critical' && !alert.acknowledged).length;
              const warningAlerts = device.alerts.filter(alert => alert.type === 'warning' && !alert.acknowledged).length;
              
              return (
                <tr key={device.deviceId} className={`device-row ${device.status}`}>
                  <td>
                    <div className="device-info">
                      <div className="device-name">{device.deviceName}</div>
                      <div className="device-id">{device.deviceId}</div>
                    </div>
                  </td>
                  
                  <td>
                    <span className="device-type">
                      {getDeviceTypeIcon(device.deviceType)} {device.deviceType}
                    </span>
                  </td>
                  
                  <td>
                    <div className="store-info">
                      <div className="store-name">{device.storeName}</div>
                      <div className="enterprise-name">{device.enterpriseName}</div>
                    </div>
                  </td>
                  
                  <td>
                    {getStatusBadge(device.status)}
                  </td>
                  
                  <td>
                    <div className="health-score">
                      <div 
                        className="health-score-bar"
                        style={{ backgroundColor: getHealthScoreColor(device.healthScore) }}
                      >
                        <div 
                          className="health-score-fill"
                          style={{ 
                            width: `${device.healthScore}%`,
                            backgroundColor: getHealthScoreColor(device.healthScore)
                          }}
                        ></div>
                      </div>
                      <span style={{ color: getHealthScoreColor(device.healthScore) }}>
                        {device.healthScore}%
                      </span>
                    </div>
                  </td>
                  
                  <td>
                    <div className="key-metrics">
                      {device.currentMetrics.batteryLevel !== undefined && (
                        <div className="metric">
                          🔋 {device.currentMetrics.batteryLevel}%
                        </div>
                      )}
                      {device.currentMetrics.memoryUsage !== undefined && (
                        <div className="metric">
                          💾 {device.currentMetrics.memoryUsage}%
                        </div>
                      )}
                      {device.currentMetrics.cpuUsage !== undefined && (
                        <div className="metric">
                          ⚡ {device.currentMetrics.cpuUsage}%
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td>
                    <div className="alerts-summary">
                      {criticalAlerts > 0 && (
                        <span className="alert-badge critical">
                          🚨 {criticalAlerts}
                        </span>
                      )}
                      {warningAlerts > 0 && (
                        <span className="alert-badge warning">
                          ⚠️ {warningAlerts}
                        </span>
                      )}
                      {criticalAlerts === 0 && warningAlerts === 0 && (
                        <span className="alert-badge none">✅</span>
                      )}
                    </div>
                  </td>
                  
                  <td>
                    <div className="last-seen">
                      {formatTimeAgo(device.lastSeen)}
                    </div>
                  </td>
                  
                  <td>
                    <button
                      onClick={() => onDeviceSelect(device)}
                      className="view-details-button"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DeviceList;